/**
 * A/B Testing Framework for Ad Placements
 * Assigns users to test variants and tracks performance
 */

const AB_TEST_KEY = 'ad_placement_variant';
const AB_TEST_METRICS_KEY = 'ad_placement_metrics';

/**
 * Get or assign A/B test variant for user
 * @returns {string} 'control' or 'variant-a' or 'variant-b'
 */
export function getAdVariant() {
  try {
    // Check if user already has a variant assigned
    let variant = localStorage.getItem(AB_TEST_KEY);
    
    if (!variant) {
      // Randomly assign variant (33% each)
      const random = Math.random();
      if (random < 0.33) {
        variant = 'control';
      } else if (random < 0.66) {
        variant = 'variant-a';
      } else {
        variant = 'variant-b';
      }
      
      localStorage.setItem(AB_TEST_KEY, variant);
    }
    
    return variant;
  } catch (error) {
    console.error('Error getting ad variant:', error);
    return 'control';
  }
}

/**
 * Get ad placement configuration based on variant
 * @param {string} variant - The A/B test variant
 * @param {string} page - The page identifier
 * @returns {object} Placement configuration
 */
export function getAdPlacementConfig(variant, page) {
  const configs = {
    control: {
      'debt-freedom-calculator': [
        { position: 'top', slot: 'auto', format: 'auto' },
        { position: 'middle', slot: 'auto', format: 'horizontal' },
        { position: 'bottom', slot: 'auto', format: 'auto' }
      ],
      'money-duration-calculator': [
        { position: 'top', slot: 'auto', format: 'auto' },
        { position: 'middle', slot: 'auto', format: 'horizontal' },
        { position: 'bottom', slot: 'auto', format: 'auto' }
      ],
      'landing': [
        { position: 'hero', slot: 'auto', format: 'horizontal' },
        { position: 'content', slot: 'auto', format: 'auto' },
        { position: 'footer', slot: 'auto', format: 'horizontal' }
      ]
    },
    'variant-a': {
      'debt-freedom-calculator': [
        { position: 'top', slot: 'auto', format: 'horizontal' },
        { position: 'sidebar', slot: 'auto', format: 'vertical' },
        { position: 'bottom', slot: 'auto', format: 'horizontal' }
      ],
      'money-duration-calculator': [
        { position: 'top', slot: 'auto', format: 'horizontal' },
        { position: 'sidebar', slot: 'auto', format: 'vertical' },
        { position: 'bottom', slot: 'auto', format: 'horizontal' }
      ],
      'landing': [
        { position: 'hero', slot: 'auto', format: 'auto' },
        { position: 'sidebar', slot: 'auto', format: 'vertical' },
        { position: 'footer', slot: 'auto', format: 'horizontal' }
      ]
    },
    'variant-b': {
      'debt-freedom-calculator': [
        { position: 'middle', slot: 'auto', format: 'horizontal' },
        { position: 'bottom', slot: 'auto', format: 'auto' }
      ],
      'money-duration-calculator': [
        { position: 'middle', slot: 'auto', format: 'horizontal' },
        { position: 'bottom', slot: 'auto', format: 'auto' }
      ],
      'landing': [
        { position: 'content', slot: 'auto', format: 'horizontal' },
        { position: 'footer', slot: 'auto', format: 'auto' }
      ]
    }
  };
  
  return configs?.[variant]?.[page] || configs?.control?.[page] || [];
}

/**
 * Track ad impression for analytics
 * @param {string} variant - The A/B test variant
 * @param {string} position - Ad position identifier
 * @param {boolean} isViewable - Whether ad was viewable
 */
export function trackAdImpression(variant, position, isViewable) {
  try {
    const metrics = JSON.parse(localStorage.getItem(AB_TEST_METRICS_KEY) || '{}');
    
    if (!metrics?.[variant]) {
      metrics[variant] = {};
    }
    
    if (!metrics?.[variant]?.[position]) {
      metrics[variant][position] = {
        impressions: 0,
        viewableImpressions: 0,
        viewabilityRate: 0
      };
    }
    
    metrics[variant][position].impressions += 1;
    if (isViewable) {
      metrics[variant][position].viewableImpressions += 1;
    }
    
    metrics[variant][position].viewabilityRate = 
      (metrics?.[variant]?.[position]?.viewableImpressions / metrics?.[variant]?.[position]?.impressions) * 100;
    
    localStorage.setItem(AB_TEST_METRICS_KEY, JSON.stringify(metrics));
  } catch (error) {
    console.error('Error tracking ad impression:', error);
  }
}

/**
 * Get ad performance metrics
 * @returns {object} Performance metrics by variant
 */
export function getAdMetrics() {
  try {
    return JSON.parse(localStorage.getItem(AB_TEST_METRICS_KEY) || '{}');
  } catch (error) {
    console.error('Error getting ad metrics:', error);
    return {};
  }
}